/**
 * The traffic incidents service implementation. Provides current traffic incidents in given region, on given zoom
 * level with given style. Current road situation is updated every minute, please use traficModelId service in
 * conjunction with your calls to get traffic incidents aligned with other services.
 *
 * The tomtom.trafficIncidents() method creates a new instance of service. Parameter can be passed on creation or
 * provided via convenient function that can be chained. The method go() performs the actual call.
 *
 * The call is asynchronous therefore user have two options to handle response, either by passing callback function or
 * by using [promise](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise). The
 * SDK provides polyfill for browsers (IE<9) without native Ecma Script 6 promise support.
 *
 * @module Services
 * @class trafficIncidents
 */
define('services/trafficIncidents',['rest/trafficIncidents', 'model/traffic/trafficIncidents', './chain', './validators', './converters', './worldEdge'],
    function(trafficIncidents, model, chain, validators, converters, worldEdge) {
        'use strict';

        var fields = {
            /**
             * A setter and getter for _'key'_ attribute. Key can be set globally when initializing map, with
             * tomtom.key or tomtom.trafficIncidents.key function. But also per service instance on initialization or
             * with this method.
             *
             * The API Key can be issued from http://developer.tomtom.com
             *
             * @method key
             * @chainable
             * @param {String} [key] API Key valid for requested service.
             * @throws {TypeError} if the given argument cannot be converted to a valid value.
             * @return {trafficIncidents|String} Method returns the service instance allowing chaining or key value if
             *     argument is omitted.
             */
            key: {validator: validators.string},
            /**
             * A setter and getter for a required _'zoomLevel'_ attribute. The zoom level of currently displayed map.
             * It is needed as the style (visibility, clustering, etc...) depends on current zoom level.
             * @method zoomLevel
             * @chainable
             * @param {Integer|String} [zoomLevel] Zoom level from 0 to 18.
             * @throws {TypeError} if the given argument cannot be converted to a valid value.
             * @return {trafficIncidents|Integer} Method returns the service instance allowing chaining or zoom level
             *     value if argument is omitted.
             */
            zoomLevel: {validator: validators.zoomLevel, required: true},
            /**
             * A setter and getter for a required _'boundingBox'_ attribute. The bounding box of the area where
             * incidents are supposed to be shown.
             *
             * Bounding box can exceed world boundaries (-180, -90, 180, 90) it is not an issue. The service will
             * internally normalize it by overwriting any exceeding value with maximal or minimal allowed. Longitude
             * will be recalculated to fit the allowed range, if bounding box includes edge of the world it will be
             * split into two bounding boxes and the response will contain merged results for both areas.
             *
             * The service is able to convert a number of popular formats into the bounding box. Supported formats are
             * listed below:
             * * _{minLon: 0, minLat: 0, maxLon: 1, maxLat: 1}_ // plain object with minLon, minLat, maxLon, maxLat
             * fields
             * * _L.latLngBounds(...)_ // leaflet bounds object
             * * _[0, 0, 1, 1]_ // array of numbers containing in order minLon, minLat, maxLon, maxLat
             * * _ol.Extent_ // open layers extend object
             * * _[ol.Coordinate, ol.Coordinate]_ // array of two open layers coordinate objects in order southWest,
             * northEast
             * * _[[0, 0], [1, 1]]_ // array of two arrays (southWest, northEast) with latitude and longitude values
             * * _"0,0,1,1"_ // comma seperated string of number values minLon, minLat, maxLon, maxLat
             * * _[{lon: 0, lat: 0},{lon: 1, lat: 1}]_ // array of two plain objects (southWest, northEast) with lat
             * and lon fields
             * * _[{lng: 0, lat: 0},{lng: 1, lat: 1}]_ // array of two plain objects (southWest, northEast) with lat
             * and lng fields
             * * _[L.latLng(...), L.latLng(...)]_ // array of southWest and northEast leaflet latLng objects
             * * _google.maps.LatLngBounds_ // Google Maps bounds object
             * * _google.maps.LatLngBoundsLiteral_ // Google Maps bounds object
             * * _[LatLng, LatLng]_ // array of two google.maps.LatLng
             * * _[LatLngLiteral, LatLngLiteral]_ // array of two google.maps.LatLngLiteral
             *
             * @method boundingBox
             * @chainable
             * @param {*} [boundingBox] the bounding box of the area that traffic incidents are supposed to be
             *     retrieved.
             * @throws {TypeError} if the given argument cannot be converted to a valid value.
             * @return {trafficIncidents|String} Method returns the service instance allowing chaining or converted
             *     bounding box value if argument is omitted.
             */
            boundingBox: {converter: converters.boundingBox, validator: validators.boundingBox, required: true},
            /**
             * A setter and getter for a required _'style'_ attribute. The style that will be used to render the
             * traffic tile in the Maps API. This will have an effect on the coordinates of traffic incidents in the
             * reply.
             *
             * @method style
             * @chainable
             * @param {String} [style] Style name, needs to be on of _'s1', 's2', 's3', 'night'_. This needs to match
             *     style of your raster traffic tiles to match traffic tubes with traffic icons.
             * @throws {TypeError} if the given argument cannot be converted to a valid value.
             * @return {trafficIncidents|String} Method returns the service instance allowing chaining or style value
             *     if argument is omitted.
             */
            style: {validator: validators.oneOfValue(['s1', 's2', 's3', 'night'], 'traffic style'), required: true},
            /**
             * A setter and getter for a _'language'_ attribute. ISO 639-1 code for the output language. Affects the
             * incidentCause and description fields in the response. By default it is English, also used in for some
             * reason conversion fails. There is a conversion mechanism in place that will (ignoring the case) try to
             * match user input with language. In example for French language the following options are possible: "fr",
             * "fre", "fr-fr", "fr_fr", "french"
             *
             * @method language
             * @chainable
             * @param {String} [language] Language code, a conversion mechanism will be applied. Supported languages
             *     are ca, cs, da, de, en, es, fi, fr, hu, it, nl, no, pl, pt, sk, sv, tr.
             * @throws {TypeError} if the given argument cannot be converted to a valid value.
             * @return {trafficIncidents|String} Method returns the service instance allowing chaining or language
             *     value if argument is omitted.
             */
            language: {converter: converters.trafficIncidentsLanguage, validator: validators.trafficIncidentsLanguage},
            /**
             * A setter and getter for a _'trafficModelID'_ attribute. Number referencing traffic model. This can be
             * obtained from trafficModelId service. It is updated every minute, and is valid for two minutes before it
             * times out. If the wrong Traffic Model ID is specified, the correct one will be returned by the
             * interface. The default value of -1 will always invoke the most recent traffic model. Nevertheless it is
             * a good practice to use value obtained from service as this will guarantee alignment with the raster
             * traffic layer.
             *
             * @method trafficModelID
             * @chainable
             * @param {Integer} [trafficModelID] Valid, not older than two minutes, traffic model ID.
             * @throws {TypeError} if the given argument cannot be converted to a valid value.
             * @return {trafficIncidents|Integer} Method returns the service instance allowing chaining or style value
             *     if argument is omitted.
             */
            trafficModelID: {validator: validators.integer, defaultValue: -1},
            geometries: {validator: validators.oneOfValue(['shifted', 'original'], 'traffic geometries')},
            /**
             * A setter and getter for an _'expandCluster'_ attribute. Enables service to list all traffic incidents in
             * a cluster separately. This causes that in the response clusters are represented as geometry collections
             * and cluster coordinates are kept as clusterCoordinates parameter. This option can be used when user
             * wants to display details of incidents that are clustered or want to perform client side clustering. By
             * default this option is disabled.
             *
             * @method expandCluster
             * @chainable
             * @param {Boolean} [expandCluster] Should service include details of incidents that are clustered.
             * @throws {TypeError} if the given argument cannot be converted to a valid value.
             * @return {trafficIncidents|Boolean} Method returns the service instance allowing chaining or expand
             *     cluster value if argument is omitted.
             */
            expandCluster: {validator: validators.bool, defaultValue: false},
            /**
             * A setter and getter for an _'originalPosition'_ attribute. Return the original position of the incident
             * (originalPosition attribute) as well as the one shifted to the beginning of the traffic tube. By default
             * _false_.
             *
             * @method originalPosition
             * @chainable
             * @param {Boolean} [originalPosition] Should service include original incidents' position.
             * @throws {TypeError} if the given argument cannot be converted to a valid value.
             * @return {trafficIncidents|Boolean} Method returns the service instance allowing chaining or original
             *     position value if argument is omitted.
             */
            originalPosition: {validator: validators.bool, defaultValue: false},
            /**
             * A setter and getter for _'protocol'_ attribute. Represents type of protocol used to perform service call.
             *
             * @method protocol
             * @chainable
             * @param {String} [protocol] Protocol type, one of _http_ or _https_.
             * @throws {TypeError} If the given argument cannot be converted to a valid value.
             * @return {trafficIncidents|String} Method returns the service instance allowing chaining or _protocol_
             * value if argument is omitted.
             */
            protocol: {validator: validators.oneOfValue(['http', 'https'], 'protocol')}
        };

        /**
         * The function performing actual call to the web service.
         *
         * Callback function takes a response parameter and fail callback takes error as only parameter.
         *
         * The final response from service is a GeoJSON with properties.
         *
         * @method go
         * @param {Function} [callback] The callback function to be called on success.
         * @param {Function} [fail] The callback function to be called on failure. If success callback is not passed
         *     fail callback is ignored. Returned promise can handle exceptional behaviour i.e. with then() method.
         * @return {Promise} If callback is omitted function returns promise
         *
         * @example
         * ```javascript
         *
         * var myFunction = function(result) { console.log(result); };
         * // this is optional if you passed global key earlier
         * tomtom.trafficIncidents.key(<Your API key>);
         * var trafficIncidents = tomtom.trafficIncidents().callback(myFunction).go();
         * ```
         */
        /**
         * A setter and getter for callback function. This function is called after receiving successful response from
         * service. It takes one argument which is the response HTML from service.
         *
         * @method callback
         * @chainable
         * @param {Function} [callback] The callback function to be called on success.
         * @throws {TypeError} if the given argument is not a function
         * @return {trafficIncidents|Function} Method returns the service instance allowing chaining or function if
         *     argument is omitted.
         */
        /**
         * A setter and getter for the fail callback function. This function is called after an unsuccessful response
         * from the service or after a communication error. It takes one argument which is the error description.
         *
         * This parameter is ignored if callback function is not passed and therefore promise mechanism is used to
         * handle the response. When using promises, errors are handled with the second attribute of the then() method
         * or with the catch() method.
         *
         * By default the fail function is:
         * ```javascript
         *
         * function(error) {
         *   console.error(error);
         *   throw new Error(error);
         * }
         * ```
         * @method fail
         * @chainable
         * @param {Function} [fail] The callback function to be called on a failure. By default, an error is logged on
         *     the console and thrown.
         * @throws {TypeError} If the given argument is not a function.
         * @return {trafficIncidents|Function} Returns the service instance that allows chaining or a function, if the argument
         *     is omitted.
         */
        function addBoundingBoxParameters(data, boundingBox) {
            data.minLon = boundingBox.minLon;
            data.maxLon = boundingBox.maxLon;
            data.minLat = boundingBox.minLat;
            data.maxLat = boundingBox.maxLat;
            return data;
        }

        function handleChain(data, callback) {
            worldEdge(trafficIncidents.incidentsDetails, model, data, callback, addBoundingBoxParameters);
        }

        /**
         * The method to instantiates the service. The options can be passed on creation or later during the execution
         * using function with the name matching parameter name. Each option can be retrieved using the exactly same
         * function but without parameter. The instance of service can be reused by calling multiple times go()
         * function.
         *
         * @method trafficIncidents
         * @param {Object} [options] Options to be passed to the trafficIncidents call.
         * @param {String} [options.key] This is optional if key was already passed.
         * @param {Integer} [options.zoomLevel] A zoom level of current map view <0, 18>.
         * @param {Object|Array|String} [options.boundingBox] The area to extract incidents from. Conversion objects
         *     are described with boudingBox() function.
         * @param {String} [options.style] Style name, needs to be on of _'s1', 's2', 's3', 'night'_. This needs to
         *     match style of your raster traffic tiles to match traffic tubes with traffic icons.
         * @param {String} [options.language=en] Language code of the incidentCause and description.
         * @param {Integer} [options.trafficModelID=-1] Valid, not older than two minutes, traffic model ID.
         * @param {Boolean} [options.expandCluster=false] Should service include details of incidents that are
         *     clustered.
         * @param {Boolean} [options.originalPosition=false] Should service include original incidents' position.
         * @param {Boolean} [options.protocol] Protocol's type, one of "http" or "https".
         * @return {trafficIncidents} Method returns new object that allows communication with the service.
         *
         * @example
         * ```javascript
         *
         * tomtom.trafficIncidents({key: "<Your API key>", boundingBox: "0,0,1,1", style: "s1"})
         *   .zoomLevel(12).go().then(function(response) {
         *     console.log(response);
         *   });
         * ```
         */
        return function(options) {
            return chain(fields, handleChain, options);
        };
    });

